import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Table, TableBody, TableCell, TableContainer, TableRow, Paper, IconButton, Tooltip, Stack, Checkbox } from '@mui/material';
import { format } from 'date-fns';
import { checkComponentPermission } from '../../../utils/share';
import Iconify from '../../../components/iconify';
import TablePaginationCustom from '../../../components/table/table-pagination-custom';
import TableHeadCustom from '../../../components/table/table-head-custom';
import TableSelectedAction from '../../../components/table/table-selected-action';
import CompOffStatusBadge from './CompOffStatusBadge';
import { COMP_OFF_STATUS } from '../constants';

const CompOffTable = ({
  compOffRequests,
  onCancelCompOff,
  onEditCompOff,
  showEmployee = false,
  currentUserId,
  count = 0,
  page = 0,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange,
  sort = {},
  onSort,
  selectedIds = [],
  selectableIds = [],
  onSelectionChange,
  selectionActions,
}) => {
  const navigate = useNavigate();
  const selectable = Boolean(onSelectionChange);

  // `id` must match the backend `sortBy` values; columns with `render` are not sortable.
  const headLabel = [
    ...(showEmployee ? [{ id: 'employee', label: 'Employee' }] : []),
    { id: 'workDate', label: 'Work Date' },
    { id: 'hoursWorked', label: 'Hours Worked' },
    { id: 'creditDays', label: 'Credit Days' },
    { id: 'status', label: 'Status' },
    ...(showEmployee
      ? [
          { id: 'reviewedBy', label: 'Reviewed By', render: () => 'Reviewed By' },
          { id: 'reviewedAt', label: 'Reviewed Date' },
        ]
      : []),
    { id: 'actions', label: 'Actions', render: () => 'Actions' },
  ];

  const selectableOnPage = (compOffRequests || [])
    .filter((compOff) => selectableIds.includes(compOff.id))
    .map((compOff) => compOff.id);

  const handleSelectAll = (checked) => onSelectionChange(checked ? selectableOnPage : []);

  const handleSelectRow = (compOffId) =>
    onSelectionChange(
      selectedIds.includes(compOffId) ? selectedIds.filter((id) => id !== compOffId) : [...selectedIds, compOffId]
    );

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd-MMM-yyyy');
    } catch (error) {
      return dateString;
    }
  };

  return (
    <>
      <TableContainer component={Paper} sx={{ position: 'relative' }}>
        {selectable && (
          <TableSelectedAction
            numSelected={selectedIds.length}
            rowCount={selectableOnPage.length}
            onSelectAllRows={handleSelectAll}
            action={selectionActions}
          />
        )}
        <Table>
          <TableHeadCustom
            headLabel={headLabel}
            order={sort.sortOrder}
            orderBy={sort.sortBy}
            onSort={onSort}
            rowCount={selectableOnPage.length}
            numSelected={selectedIds.length}
            onSelectAllRows={selectable ? handleSelectAll : undefined}
          />
          <TableBody>
            {compOffRequests && compOffRequests.length > 0 ? (
              compOffRequests.map((compOff) => (
                <TableRow key={compOff.id} hover selected={selectedIds.includes(compOff.id)}>
                  {selectable && (
                    <TableCell padding="checkbox">
                      {selectableIds.includes(compOff.id) && (
                        <Checkbox
                          checked={selectedIds.includes(compOff.id)}
                          onChange={() => handleSelectRow(compOff.id)}
                        />
                      )}
                    </TableCell>
                  )}
                  {showEmployee && (
                    <TableCell>{compOff.employee?.userName || compOff.employee?.email || '-'}</TableCell>
                  )}
                  <TableCell>{formatDate(compOff.workDate)}</TableCell>
                  <TableCell>{compOff.hoursWorked}h</TableCell>
                  <TableCell>{compOff.creditDays != null ? `${compOff.creditDays}d` : '-'}</TableCell>
                  <TableCell>
                    <CompOffStatusBadge status={compOff.status} />
                  </TableCell>
                  {showEmployee && (
                    <TableCell>
                      {compOff.approver && compOff.approver.id === currentUserId
                        ? 'Me'
                        : compOff.approver?.userName || compOff.approver?.email || '-'}
                    </TableCell>
                  )}
                  {showEmployee && <TableCell>{formatDate(compOff.reviewedAt)}</TableCell>}
                  <TableCell align="left">
                    <Stack direction="row" spacing={1} justifyContent="left">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          color="info"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/comp-off/${compOff.id}`);
                          }}
                        >
                          <Iconify icon="eva:eye-fill" />
                        </IconButton>
                      </Tooltip>
                      {!showEmployee && compOff.status === COMP_OFF_STATUS.PENDING && compOff.userId === currentUserId && (
                        <>
                          {checkComponentPermission('UPDATE_COMP_OFF') && (
                            <Tooltip title="Edit Comp-Off">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEditCompOff(compOff.id);
                                }}
                              >
                                <Iconify icon="eva:edit-fill" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {checkComponentPermission('DELETE_COMP_OFF') && (
                            <Tooltip title="Delete Comp-Off">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onCancelCompOff(compOff.id);
                                }}
                              >
                                <Iconify icon="material-symbols:delete" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={headLabel.length + (selectable ? 1 : 0)} align="center">
                  No comp-off requests found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {count > 0 && (
        <TablePaginationCustom
          count={count}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      )}
    </>
  );
};

CompOffTable.propTypes = {
  compOffRequests: PropTypes.array.isRequired,
  onCancelCompOff: PropTypes.func,
  onEditCompOff: PropTypes.func,
  showEmployee: PropTypes.bool,
  currentUserId: PropTypes.number,
  count: PropTypes.number,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  onPageChange: PropTypes.func,
  onRowsPerPageChange: PropTypes.func,
  sort: PropTypes.shape({ sortBy: PropTypes.string, sortOrder: PropTypes.oneOf(['asc', 'desc']) }),
  onSort: PropTypes.func,
  selectedIds: PropTypes.arrayOf(PropTypes.number),
  selectableIds: PropTypes.arrayOf(PropTypes.number),
  onSelectionChange: PropTypes.func,
  selectionActions: PropTypes.node,
};

export default CompOffTable;
